import { useState, useEffect } from 'react';
// Use ProductDataService for unified geogrid product database
import { ProductDataService } from '../../services/ProductDataService';
import { useAppSelector } from '../../../reduxService/hooks';
import { RootState } from '../../../reduxService';
import { EProjectType } from '../../enum/project';

export interface SelectOption {
    value: string | number;
    label: string;
}

export interface GeogridData {
    category?: string;
    subCategory?: string;
    tensileMax?: number;
    tensile1Percent?: number;
    tensile2Percent?: number;
    tensile3Percent?: number;
    tensile4Percent?: number;
    tensile5Percent?: number;
    torsionalStiffness?: number;
    [key: string]: any;
}

export interface GeogridDataset {
    [key: string]: GeogridData;
}

/**
 * Simula una query MongoDB per filtrare i dati dei prodotti
 */
interface QueryFilter {
    keyStartsWith?: string;
    keyNotStartsWith?: string;
    /** Filter by product category (GEOGRID or GEOTEXTILE) */
    category?: 'GEOGRID' | 'GEOTEXTILE';
}

/**
 * Valid project types for product filtering
 */
type ProjectTypeFilter = 'airport' | 'paved' | 'unpaved' | 'railways' | 'container_yard' | 'parking_deck';

/**
 * Carica i dati dei prodotti geogrid usando ProductDataService (unified database)
 * Supporta filtri per nome prodotto, categoria e tipo progetto
 *
 * @param filter - Filtro opzionale per nome prodotto (keyStartsWith, keyNotStartsWith) e categoria
 * @param projectType - Tipo progetto opzionale per filtrare prodotti con applicableProjects
 * @returns Lista di opzioni select per dropdown
 */
export const getUnpavedGiroudDataAsync = async (filter?: QueryFilter, projectType?: ProjectTypeFilter): Promise<SelectOption[]> => {
    try {
        // Build filter options for ProductDataService
        const filterOptions: { projectType?: ProjectTypeFilter; category?: 'GEOGRID' | 'GEOTEXTILE' } = {};
        if (projectType) filterOptions.projectType = projectType;
        if (filter?.category) filterOptions.category = filter.category;

        // Get products with all filters applied (projectType for applicableProjects, category for GEOGRID/GEOTEXTILE)
        const allProducts = Object.keys(filterOptions).length > 0
            ? ProductDataService.getProductsWithFilters(filterOptions)
            : ProductDataService.getAllProducts();

        // Flatten structure: { "Family": [ { prodotto: "NAME" } ] } -> [ "NAME1", "NAME2", ... ]
        const allProductNames: string[] = [];
        for (const family of Object.values(allProducts)) {
            for (const product of family) {
                allProductNames.push(product.prodotto);
            }
        }

        // Apply name-based filters (for backward compatibility)
        let filteredNames = allProductNames;

        if (filter) {
            if (filter.keyStartsWith) {
                filteredNames = filteredNames.filter(name => name.startsWith(filter.keyStartsWith!));
            }

            if (filter.keyNotStartsWith) {
                filteredNames = filteredNames.filter(name => !name.startsWith(filter.keyNotStartsWith!));
            }
        }

        // Transform to select options format
        const options = filteredNames.map(name => ({
            value: name,
            label: name,
        }));

        return options;
    } catch (error) {
        console.error('getUnpavedGiroudDataAsync: Error loading products from unified database', error);
        // Return a fallback option if loading fails
        const fallbackValue = filter?.category === 'GEOTEXTILE' ? 'GEOTEXTILE' : 'MACGRID WG 3S';
        return [{ value: fallbackValue, label: fallbackValue }];
    }
};

/**
 * Maps EProjectType enum to valid filter string for applicableProjects filtering
 * Only certain project types have product filtering enabled
 */
const mapProjectTypeToFilter = (projectType?: EProjectType): ProjectTypeFilter | undefined => {
    if (!projectType) return undefined;

    // Map enum values to filter strings (only valid project types for product filtering)
    const validFilterTypes: Record<string, ProjectTypeFilter> = {
        [EProjectType.airport]: 'airport',
        [EProjectType.paved]: 'paved',
        [EProjectType.unpaved]: 'unpaved',
        [EProjectType.railways]: 'railways',
        [EProjectType.container_yard]: 'container_yard',
        [EProjectType.parking_deck]: 'parking_deck',
    };

    return validFilterTypes[projectType];
};

/**
 * Hook personalizzato per gestire il caricamento dinamico delle opzioni per i campi select
 * Automatically filters products by project type using applicableProjects from unified database
 */
export const useDynamicSelectOptions = (optionsSource?: string, defaultOptions?: SelectOption[]) => {
    const [options, setOptions] = useState<SelectOption[]>(defaultOptions || []);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Get current project type from Redux store for product filtering
    const projectType = useAppSelector((state: RootState) => state.Dynamic.project?.data?.type) as EProjectType | undefined;
    const projectTypeFilter = mapProjectTypeToFilter(projectType);

    useEffect(() => {
        let isCancelled = false; // Cancellation flag to prevent race conditions

        if (!optionsSource) {
            setOptions(defaultOptions || []);
            return;
        }

        const loadOptions = async () => {
            if (isCancelled) return; // Early exit if cancelled

            setLoading(true);
            setError(null);

            try {
                let loadedOptions: SelectOption[] = [];

                switch (optionsSource) {
                    case 'unpavedGiroudData':
                        // Per selectedGeogrid: tutti i prodotti con categoria GEOGRID
                        // Filter by projectType to respect applicableProjects in unified database
                        loadedOptions = await getUnpavedGiroudDataAsync(
                            { category: 'GEOGRID' },
                            projectTypeFilter
                        );
                        break;
                    case 'unpavedGiroudGeotextiles':
                        // Per selectedGeotextile: tutti i prodotti con categoria GEOTEXTILE
                        // Filter by projectType to respect applicableProjects in unified database
                        loadedOptions = await getUnpavedGiroudDataAsync(
                            { category: 'GEOTEXTILE' },
                            projectTypeFilter
                        );
                        break;
                    case 'railwayGeogridsData':
                        // Per progetti Railway: usa filtro sul database globale per MacGrid EG solo
                        // Secondo specifiche: Railway supporta SOLO famiglia MacGrid EG
                        loadedOptions = await getUnpavedGiroudDataAsync(
                            { keyStartsWith: 'MACGRID EG', category: 'GEOGRID' },
                            projectTypeFilter
                        );
                        break;
                    default:
                        loadedOptions = defaultOptions || [];
                        break;
                }

                // Only update state if not cancelled
                if (!isCancelled) {
                    setOptions(loadedOptions);
                }
            } catch (err) {
                if (!isCancelled) {
                    setError(err instanceof Error ? err.message : 'Failed to load options');
                    // Fallback to default options on error
                    setOptions(defaultOptions || []);
                }
            } finally {
                if (!isCancelled) {
                    setLoading(false);
                }
            }
        };

        loadOptions();

        // Cleanup function to prevent race conditions
        return () => {
            isCancelled = true;
        };
    }, [optionsSource, defaultOptions, projectTypeFilter]);

    return { options, loading, error };
};
